type ValidationSummaryLevel = "warning" | "error" | "success" | "info";

interface IValidationSummaryContent {
    title: string;
    body: string;
    level: ValidationSummaryLevel;
    htmlContent?: HTMLElement
}

/**
 * Make validation summary element hidden
 *
 * @param validationSummaryElement The validation message to hide
 */
function hideValidationSummary(validationSummaryElement: HTMLElement) {
    if (validationSummaryElement === null || validationSummaryElement === undefined) return;
    validationSummaryElement.setAttribute("aria-hidden", "true");
    validationSummaryElement.removeAttribute("role");
    validationSummaryElement.className = "validation-summary";
    validationSummaryElement.classList.add("hidden");
}

/**
 * Make validation summary element visible
 *
 * @param validationSummaryElement The validation message to display
 */
function showValidationSummary(validationSummaryElement: HTMLElement) {
    if (validationSummaryElement === null || validationSummaryElement === undefined) return;
    validationSummaryElement.removeAttribute("aria-hidden");
    validationSummaryElement.setAttribute("role", "alert");
    validationSummaryElement.classList.remove("hidden");
}

/**
 * Display or hide validation message of existing partial. Pass content to be populated
 * Populate parameter to display.
 * Leave parameter null to hide.
 *
 * @param {IValidationSummaryContent} content
 */
export function validationSummary(content?: IValidationSummaryContent, validationElement?: HTMLElement) {
    const validationSummaryElement = validationElement ?? document.querySelector<HTMLElement>(".validation-summary");
    if (validationSummaryElement === null || validationSummaryElement === undefined) return;
    if (content !== null && content !== undefined) {
        validationSummaryElement.classList.add(`validation-${content.level}`);
        const contentText = stringIsEmpty(content.title) ? content.body : `${content.title} ${content.body}`;
        validationSummaryContent(validationSummaryElement, contentText, content.htmlContent);
        showValidationSummary(validationSummaryElement);
        scrollToValidationSummary(validationSummaryElement)
    }
    else {
        validationSummaryContent(validationSummaryElement);
        hideValidationSummary(validationSummaryElement);
    }
}

/**
 * Scroll the viewport to the validation summary element
 * @param validationSummaryElement
 */
function scrollToValidationSummary(validationSummaryElement: HTMLElement){
    if (!validationSummaryElement){
        return;
    }
    validationSummaryElement.scrollIntoView({behavior: "smooth"});
}

/**
 * check string is empty
 *
 * @param {string} stringValue
 */
function stringIsEmpty(stringValue: string) {
    return (stringValue === null || stringValue === undefined || !stringValue || stringValue.length === 0);
}

/**
 * Update validation summary content. Pass parent element with content to be populated
 * Populate parameter to set content to inputted values.
 * Leave parameter null to set content to null.
 *
 * @param {HTMLElement} element // parent element
 * @param {string} content
 */
function validationSummaryContent(element: HTMLElement, content?: string, contentHTML?: HTMLElement) {
    if (element !== null && element !== undefined) {
        const contentElement = element.querySelector<HTMLElement>(".validation-summary__content");
        if (contentElement === null || contentElement === undefined) return;
        if (content !== null && content !== "" && content !== undefined) {
            contentElement.innerText = content;
        }
        else {
            contentElement.innerText = "";
        }

        const html = element.querySelector('.validation-summary__content--html');
        if (html != null) {
            html.remove();
        }
        if (contentHTML != null) {
            element.appendChild(contentHTML);
        }
    }
}
